import React, { useState, useEffect, useRef } from 'react';
import { useCookies } from 'react-cookie';
import './Chat.css';

// Use same API base as Topbar
const API = process.env.REACT_APP_API || 'https://api.industriegaming.fr';

export default function ChatPopup() {
    const [open, setOpen] = useState(false);
    const [conversation, setConversation] = useState(null);
    const conversationRef = useRef(conversation);
    const [messages, setMessages] = useState([]);
    const [unread, setUnread] = useState(0);
    const [input, setInput] = useState('');
    const [typing, setTyping] = useState(false);
    const socketRef = useRef(null);
    const audioCtxRef = useRef(null);
    const pendingConvRef = useRef(null);
    const [cookies] = useCookies(["token"]);
    const visitorIdRef = useRef(localStorage.getItem('visitorId') || `v_${Date.now()}`);

    function decodeToken(token) {
        try {
            const payload = token.split('.')[1];
            return JSON.parse(atob(payload));
        } catch {
            return null;
        }
    }

    const playNotificationSound = () => {
        try {
            // create audio context lazily
            if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            const ctx = audioCtxRef.current;
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'sine';
            o.frequency.value = 880; // A5
            g.gain.value = 0;
            o.connect(g);
            g.connect(ctx.destination);
            const now = ctx.currentTime;
            g.gain.setValueAtTime(0, now);
            g.gain.linearRampToValueAtTime(0.12, now + 0.01);
            o.start(now);
            g.gain.linearRampToValueAtTime(0.0001, now + 0.25);
            o.stop(now + 0.26);
        } catch (e) {
            // fallback: try Audio element beep (not added), ignore errors
            console.warn('notification sound failed', e);
        }
    };

    useEffect(() => {
        conversationRef.current = conversation;
    }, [conversation]);

    useEffect(() => {
        let mounted = true;
        localStorage.setItem('visitorId', visitorIdRef.current);

        (async () => {
            try {
                const mod = await import('socket.io-client');
                const ioClient = mod.io || mod.default || mod;
                if (!mounted) return;
                // force polling transport when websocket upgrade fails (reverse-proxy issues)
                socketRef.current = ioClient(API, { transports: ['polling'], path: '/socket.io', secure: true });
                socketRef.current.on('connect', () => {
                    console.log('visitor socket connected', socketRef.current.id);
                    socketRef.current.emit('identify', { role: 'visitor' });
                    console.log('visitor emitted identify');
                    if (conversation) {
                        console.log('visitor emitting joinConv', conversation.id);
                        socketRef.current.emit('joinConv', { conversationId: conversation.id });
                    }
                });

                socketRef.current.on('connect_error', (err) => {
                    console.error('visitor socket connect_error', err);
                });
                socketRef.current.on('disconnect', (reason) => {
                    console.log('visitor socket disconnected', reason);
                });

                socketRef.current.on('chat:message', ({ conversationId, message }) => {
                    console.log('visitor chat:message received', conversationId, message);
                    const convNow = conversationRef.current;
                    // If we have the conversation loaded and it matches, append the message
                    if (convNow && String(convNow.id) === String(conversationId)) {
                        setMessages(prev => {
                            if (!message || prev.some(m => String(m.id) === String(message.id))) return prev;
                            return [...prev, message];
                        });
                        // play sound for incoming admin messages
                        if (message && message.sender && String(message.sender) !== 'visitor') playNotificationSound();
                        if (!open) setUnread(u => u + 1);
                        return;
                    }

                    // If the conversation isn't loaded yet, mark there is a pending conversation
                    // and increment the unread counter so the visitor sees there's a new message.
                    pendingConvRef.current = conversationId;
                    setUnread(u => u + 1);
                    // play sound when a new conversation message arrives while not opened
                    if (message && message.sender && String(message.sender) !== 'visitor') playNotificationSound();
                });

                socketRef.current.on('chat:typing', ({ conversationId, sender, typing }) => {
                    const convNow = conversationRef.current;
                    if (convNow && String(convNow.id) === String(conversationId)) {
                        setTyping(typing);
                    }
                });

                socketRef.current.on('chat:conversation:update', ({ conversation: conv }) => {
                    try {
                        const convNow = conversationRef.current;
                        if (convNow && String(convNow.id) === String(conv.id)) {
                            setConversation(conv);
                        }
                    } catch (e) {
                        // ignore
                    }
                });

                socketRef.current.on('chat:conversation:closed', ({ conversationId }) => {
                    try {
                        const convNow = conversationRef.current;
                        if (convNow && String(convNow.id) === String(conversationId)) {
                            setConversation(prev => ({ ...(prev || {}), status: 'closed' }));
                        }
                    } catch (e) {
                        // ignore
                    }
                });

                // On mount, try to restore existing conversation for this visitor
                try {
                    const headers = {};
                    if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;
                    const r = await fetch(`${API}/api/chat/visitor/${visitorIdRef.current}`, { headers });
                    if (r.ok) {
                        const conv = await r.json();
                        if (mounted) {
                            setConversation(conv);
                            // ensure socket connected then join
                            if (socketRef.current) {
                                try {
                                    await waitForSocketConnect(socketRef.current);
                                    console.debug('emitting joinConv for visitor', conv.id);
                                    socketRef.current.emit('joinConv', { conversationId: conv.id });
                                } catch (e) {
                                    console.warn('socket connect timeout before joinConv', e);
                                }
                            }
                            const mres = await fetch(`${API}/api/chat/${conv.id}/messages`, { headers });
                            if (mres.ok) {
                                const msgs = await mres.json();
                                setMessages(msgs);
                            }
                        }
                    }
                } catch (e) {
                    // ignore missing conversation
                }
            } catch (e) {
                console.error('socket.io-client load error', e);
            }
        })();

        return () => {
            mounted = false;
            if (socketRef.current && socketRef.current.disconnect) socketRef.current.disconnect();
        };
    }, []);

    // helper: wait until socket is connected (with timeout)
    function waitForSocketConnect(socket, timeout = 5000) {
        return new Promise((resolve, reject) => {
            if (!socket) return reject(new Error('no socket'));
            if (socket.connected) return resolve();
            const onConnect = () => {
                cleanup();
                resolve();
            };
            const onError = (err) => {
                cleanup();
                reject(err || new Error('connect error'));
            };
            const timer = setTimeout(() => {
                cleanup();
                reject(new Error('connect timeout'));
            }, timeout);
            function cleanup() {
                clearTimeout(timer);
                socket.off('connect', onConnect);
                socket.off('connect_error', onError);
            }
            socket.on('connect', onConnect);
            socket.on('connect_error', onError);
        });
    }

    const openConversation = async () => {
        if (!conversation) {
            try {
                const headers = { 'Content-Type': 'application/json' };
                if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;

                // First try to restore an existing conversation for this visitor
                const r = await fetch(`${API}/api/chat/visitor/${visitorIdRef.current}`, { headers });
                if (r.ok) {
                    const conv = await r.json();
                    setConversation(conv);
                    // join conv
                    if (socketRef.current) {
                        try {
                            await waitForSocketConnect(socketRef.current);
                            socketRef.current.emit('joinConv', { conversationId: conv.id });
                        } catch (e) {
                            // ignore
                        }
                    }
                    const mres = await fetch(`${API}/api/chat/${conv.id}/messages`, { headers });
                    if (mres.ok) {
                        const msgs = await mres.json();
                        setMessages(msgs);
                    }
                    } else if (pendingConvRef.current) {
                    // A message arrived for a conversation we don't yet have loaded.
                    // Try to load messages for that conversation id and set conversation id.
                    const convId = pendingConvRef.current;
                    try {
                        if (socketRef.current) {
                            try {
                                await waitForSocketConnect(socketRef.current);
                                socketRef.current.emit('joinConv', { conversationId: convId });
                            } catch (e) {
                                // ignore
                            }
                        }
                        const mres = await fetch(`${API}/api/chat/${convId}/messages`, { headers });
                        if (mres.ok) {
                            const msgs = await mres.json();
                            // fetch conversation details (status/title)
                            try {
                                const cres = await fetch(`${API}/api/chat/${convId}`, { headers });
                                if (cres.ok) {
                                    const convDetails = await cres.json();
                                    setConversation(convDetails);
                                } else {
                                    setConversation({ id: convId });
                                }
                            } catch (e) {
                                setConversation({ id: convId });
                            }
                            setMessages(msgs);
                        } else {
                            // If that also fails, fall back to creating a new conversation
                            throw new Error('no conv by pending id');
                        }
                    } catch (e) {
                        // fallback to create
                        // Determine a sensible title: use logged-in user's name if available
                        let title = 'Visitor';
                        try {
                            const token = cookies.token;
                            if (token) {
                                const payload = decodeToken(token);
                                if (payload && payload.name) title = payload.name;
                            }
                        } catch (e) {
                            // ignore
                        }

                        const res = await fetch(`${API}/api/chat/start`, {
                            method: 'POST',
                            headers,
                            body: JSON.stringify({ title, visitorId: visitorIdRef.current })
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const conv = await res.json();
                        setConversation(conv);
                        if (socketRef.current) {
                            try {
                                await waitForSocketConnect(socketRef.current);
                                socketRef.current.emit('joinConv', { conversationId: conv.id });
                            } catch (e) {
                                // ignore join if can't connect now
                            }
                        }
                        const mres2 = await fetch(`${API}/api/chat/${conv.id}/messages`, { headers });
                        if (mres2.ok) {
                            const msgs = await mres2.json();
                            setMessages(msgs);
                        }
                    }
                } else {
                    // No existing conversation, create a new one
                    // Determine title from token if available
                    let title = 'Visitor';
                    try {
                        const token = cookies.token;
                        if (token) {
                            const payload = decodeToken(token);
                            if (payload && payload.name) title = payload.name;
                        }
                    } catch (e) {
                        // ignore
                    }
                    const res = await fetch(`${API}/api/chat/start`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ title, visitorId: visitorIdRef.current })
                    });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const conv = await res.json();
                    setConversation(conv);
                    if (socketRef.current) {
                        try {
                            await waitForSocketConnect(socketRef.current);
                            socketRef.current.emit('joinConv', { conversationId: conv.id });
                        } catch (e) {
                            // ignore join if can't connect now
                        }
                    }
                    const mres = await fetch(`${API}/api/chat/${conv.id}/messages`, { headers });
                    if (mres.ok) {
                        const msgs = await mres.json();
                        setMessages(msgs);
                    }
                }
            } catch (err) {
                console.error('openConversation error', err);
                alert('Impossible de joindre le serveur de chat.');
                return;
            }
        }
        setOpen(true);
        setUnread(0);
    };

    const startNewConversation = async () => {
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;

            // Determine title from token if available
            let title = 'Visitor';
            try {
                const token = cookies.token;
                if (token) {
                    const payload = decodeToken(token);
                    if (payload && payload.name) title = payload.name;
                }
            } catch (e) {}

            const res = await fetch(`${API}/api/chat/start`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ title, visitorId: visitorIdRef.current })
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const conv = await res.json();
            // clear any pending conv marker
            pendingConvRef.current = null;
            setConversation(conv);
            // load any existing messages (in case an admin replied immediately)
            try {
                const mresNew = await fetch(`${API}/api/chat/${conv.id}/messages`, { headers });
                if (mresNew.ok) {
                    const msgsNew = await mresNew.json();
                    setMessages(msgsNew);
                } else {
                    setMessages([]);
                }
            } catch (e) {
                setMessages([]);
            }
            if (socketRef.current) {
                try {
                    await waitForSocketConnect(socketRef.current);
                    socketRef.current.emit('joinConv', { conversationId: conv.id });
                } catch (e) {
                    // ignore
                }
            }
            setOpen(true);
            setUnread(0);
        } catch (err) {
            console.error('startNewConversation error', err);
            alert('Impossible de démarrer une nouvelle conversation.');
        }
    };

    const sendMessage = async () => {
        if (!input.trim() || !conversation) return;
        if (conversation && String(conversation.status) === 'closed') {
            alert('Cette conversation est clôturée.');
            return;
        }
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;

            const res = await fetch(`${API}/api/chat/${conversation.id}/message`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ sender: 'visitor', content: input })
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const msg = await res.json();
            console.debug('sendMessage response', msg);
            setMessages(prev => {
                if (!msg || prev.some(m => String(m.id) === String(msg.id))) return prev;
                return [...prev, msg];
            });
            // rely on socket emission as well but dedupe prevents duplication
            setInput('');
        } catch (err) {
            console.error('sendMessage error', err);
            alert('Impossible d\'envoyer le message.');
        }
    };

    const handleInput = (e) => {
        setInput(e.target.value);
        socketRef.current?.emit('chat:typing', { conversationId: conversation?.id, sender: 'visitor', typing: true });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
            socketRef.current?.emit('chat:typing', { conversationId: conversation?.id, sender: 'visitor', typing: false });
        }, 1000);
    };

    const handleKeyDown = (e) => {
        // Enter to send, Shift+Enter to insert newline
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            try { socketRef.current?.emit('chat:typing', { conversationId: conversation?.id, sender: 'visitor', typing: false }); } catch (err) {}
            sendMessage();
        }
    };

    const typingTimeoutRef = useRef(null);
    const chatBodyRef = useRef(null);

    const scrollToBottom = () => {
        const el = chatBodyRef.current;
        if (!el) return;
        // wait a tick for DOM to render
        requestAnimationFrame(() => {
            try {
                el.scrollTop = el.scrollHeight;
            } catch (e) {
                // ignore
            }
        });
    };

    useEffect(() => {
        if (open) scrollToBottom();
    }, [messages, open]);

    return (
        <div>
            <div className="chat-popup-toggle" onClick={() => openConversation()}>
                💬
                {unread > 0 && <span className="unread-badge">{unread}</span>}
            </div>

            {open && (
                <div className="chat-window">
                    <div className="chat-header">
                        <span>Support</span>
                        <button className="chat-close" aria-label="Fermer la chat" onClick={() => setOpen(false)}>✕</button>
                    </div>
                    <div className="chat-body" ref={chatBodyRef}>
                        {messages.map(m => (
                            <div key={m.id} className={`chat-message ${m.sender === 'visitor' ? 'me' : 'them'}`}>
                                <div className="content">{m.content}</div>
                            </div>
                        ))}
                        {typing && <div className="typing">• • •</div>}
                    </div>
                    {conversation && String(conversation.status) === 'closed' && (
                        <div style={{ padding:8, textAlign:'center', background:'#fff3f2', color:'#7a1f1f', borderTop:'1px solid #f0d6d6' }}>
                            <div>Conversation clôturée</div>
                            <button onClick={startNewConversation} style={{ marginTop:8, padding:'6px 10px', borderRadius:6, background:'#007bff', color:'#fff', border:'none' }}>Nouvelle conversation</button>
                        </div>
                    )}
                    <div className="chat-input">
                        <input value={input} onChange={handleInput} onKeyDown={handleKeyDown} placeholder="Écrire un message..." disabled={conversation && String(conversation.status) === 'closed'} />
                        <button onClick={sendMessage} disabled={conversation && String(conversation.status) === 'closed'}>Envoyer</button>
                    </div>
                </div>
            )}
        </div>
    );
}
